import React, { useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, Check } from "lucide-react";

export default function CameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [ready, setReady] = useState(false);

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  };

  const start = async (mode = facingMode) => {
    setError("");
    setReady(false);
    try {
      stop();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        setReady(true);
      }
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Camera permission denied. Please allow camera access in your browser."
          : e?.name === "NotFoundError"
          ? "No camera found on this device."
          : "Unable to start the camera."
      );
    }
  };

  useEffect(() => {
    if (open) {
      start(facingMode);
    } else {
      stop();
    }
    return () => stop();
    // eslint-disable-next-line
  }, [open]);

  const flip = () => {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    start(next);
  };

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) return;
    // Cap max dimension for reasonable payload size
    const MAX = 1600;
    const scale = Math.min(1, MAX / Math.max(w, h));
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: "image/jpeg" });
        onCapture(file);
        stop();
        onClose();
      },
      "image/jpeg",
      0.9
    );
  };

  if (!open) return null;

  return (
    <div
      data-testid="camera-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-up"
    >
      <div className="w-full max-w-3xl rounded-xl bg-zinc-950 text-white overflow-hidden border border-zinc-800">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-zinc-300" />
            <span className="font-display text-sm font-semibold tracking-tight">Capture receipt</span>
          </div>
          <button
            data-testid="camera-close-btn"
            onClick={() => {
              stop();
              onClose();
            }}
            className="p-1.5 rounded-md text-zinc-300 hover:bg-zinc-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative bg-black aspect-[4/3] flex items-center justify-center">
          {error ? (
            <div data-testid="camera-error" className="text-sm text-red-300 px-6 text-center">
              {error}
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full h-full object-contain"
                data-testid="camera-video"
              />
              {!ready ? (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-300">
                  Starting camera…
                </div>
              ) : null}
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-zinc-800">
          <button
            data-testid="camera-flip-btn"
            onClick={flip}
            disabled={!ready}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs hover:bg-zinc-800 disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Flip camera
          </button>
          <button
            data-testid="camera-snap-btn"
            onClick={snap}
            disabled={!ready}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white text-zinc-950 px-5 py-2.5 text-sm font-medium hover:bg-zinc-100 disabled:opacity-50"
          >
            <Check className="w-4 h-4" /> Snap & scan
          </button>
        </div>
      </div>
    </div>
  );
}
